// ╔══════════════════════════════════════════════════════════════════╗
// ║               SWIFTGOLDFIDUCIARY CAPTCHA COMPONENT                ║
// ║  Puzzle slider captcha with banking themed scenes                 ║
// ╚══════════════════════════════════════════════════════════════════╝

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { RefreshCw, Check, X, Shield, ChevronRight, Lock } from 'lucide-react'

export default function SwiftGoldCaptcha({ onVerified, onFailed }) {
  const [isVerified, setIsVerified] = useState(false)
  const [isFailed, setIsFailed] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [sliderPosition, setSliderPosition] = useState(0)
  const [targetPosition, setTargetPosition] = useState({ x: 180, y: 45 })
  const [attempts, setAttempts] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [sceneIndex, setSceneIndex] = useState(0)

  const containerRef = useRef(null)

  const pieceSize = 48
  const tolerance = 6
  const maxSliderWidth = 252

  // Banking themed SVG scenes
  const scenes = [
    // Scene 1: Credit Card
    {
      id: 'card',
      background: (
        <>
          <defs>
            <linearGradient id="cardBg" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#0f172a" />
              <stop offset="100%" stopColor="#1e3a5f" />
            </linearGradient>
            <linearGradient id="cardGold" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#fbbf24" />
              <stop offset="50%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#d97706" />
            </linearGradient>
            <linearGradient id="cardShine" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(255,255,255,0)" />
              <stop offset="50%" stopColor="rgba(255,255,255,0.1)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </linearGradient>
          </defs>
          <rect width="340" height="180" fill="url(#cardBg)" />
          <circle cx="300" cy="30" r="80" fill="rgba(59,130,246,0.1)" />
          <circle cx="40" cy="150" r="60" fill="rgba(59,130,246,0.1)" />
          <g transform="translate(70, 35)">
            <rect x="0" y="0" width="200" height="120" rx="12" fill="url(#cardGold)" />
            <rect x="0" y="0" width="200" height="120" rx="12" fill="url(#cardShine)" />
            <rect x="25" y="35" width="35" height="28" rx="4" fill="#d4af37" stroke="#b8860b" strokeWidth="1" />
            <line x1="25" y1="45" x2="60" y2="45" stroke="#b8860b" strokeWidth="0.5" />
            <line x1="25" y1="53" x2="60" y2="53" stroke="#b8860b" strokeWidth="0.5" />
            <line x1="42" y1="35" x2="42" y2="63" stroke="#b8860b" strokeWidth="0.5" />
            <g fill="#1e293b" opacity="0.7">
              <circle cx="25" cy="82" r="3" />
              <circle cx="35" cy="82" r="3" />
              <circle cx="45" cy="82" r="3" />
              <circle cx="55" cy="82" r="3" />
              <circle cx="75" cy="82" r="3" />
              <circle cx="85" cy="82" r="3" />
              <circle cx="95" cy="82" r="3" />
              <circle cx="105" cy="82" r="3" />
              <circle cx="125" cy="82" r="3" />
              <circle cx="135" cy="82" r="3" />
              <circle cx="145" cy="82" r="3" />
              <circle cx="155" cy="82" r="3" />
            </g>
            <text x="170" y="86" fill="#1e293b" fontSize="12" fontFamily="monospace" fontWeight="bold">1234</text>
            <g transform="translate(165, 35)" stroke="#1e293b" strokeWidth="1.5" fill="none" opacity="0.6">
              <path d="M0,12 Q6,6 0,0" />
              <path d="M5,12 Q11,6 5,0" />
              <path d="M10,12 Q16,6 10,0" />
            </g>
            <text x="25" y="25" fill="#1e293b" fontSize="11" fontWeight="bold" fontFamily="system-ui">SWIFTGOLD</text>
            <text x="80" y="105" fill="#1e293b" fontSize="7" opacity="0.6">VALID THRU</text>
            <text x="80" y="115" fill="#1e293b" fontSize="10" fontWeight="bold">12/28</text>
          </g>
        </>
      )
    },
    // Scene 2: Financial Growth Chart
    {
      id: 'growth',
      background: (
        <>
          <defs>
            <linearGradient id="growthBg" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#0f172a" />
              <stop offset="100%" stopColor="#1e293b" />
            </linearGradient>
            <linearGradient id="chartGreen" x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" stopColor="rgba(34,197,94,0)" />
              <stop offset="100%" stopColor="rgba(34,197,94,0.3)" />
            </linearGradient>
            <linearGradient id="lineGreen" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#22c55e" />
              <stop offset="100%" stopColor="#4ade80" />
            </linearGradient>
          </defs>
          <rect width="340" height="180" fill="url(#growthBg)" />
          <g stroke="rgba(255,255,255,0.05)" strokeWidth="1">
            <line x1="40" y1="30" x2="40" y2="150" />
            <line x1="100" y1="30" x2="100" y2="150" />
            <line x1="160" y1="30" x2="160" y2="150" />
            <line x1="220" y1="30" x2="220" y2="150" />
            <line x1="280" y1="30" x2="280" y2="150" />
            <line x1="40" y1="50" x2="300" y2="50" />
            <line x1="40" y1="80" x2="300" y2="80" />
            <line x1="40" y1="110" x2="300" y2="110" />
            <line x1="40" y1="140" x2="300" y2="140" />
          </g>
          <path d="M40,140 L70,130 L100,120 L130,115 L160,95 L190,85 L220,60 L250,50 L280,35 L300,30 L300,150 L40,150 Z" fill="url(#chartGreen)" />
          <path d="M40,140 L70,130 L100,120 L130,115 L160,95 L190,85 L220,60 L250,50 L280,35 L300,30" fill="none" stroke="url(#lineGreen)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          <g fill="#22c55e">
            <circle cx="40" cy="140" r="4" />
            <circle cx="100" cy="120" r="4" />
            <circle cx="160" cy="95" r="4" />
            <circle cx="220" cy="60" r="4" />
            <circle cx="280" cy="35" r="4" />
          </g>
          <g fill="#0f172a" stroke="#22c55e" strokeWidth="2">
            <circle cx="40" cy="140" r="3" />
            <circle cx="100" cy="120" r="3" />
            <circle cx="160" cy="95" r="3" />
            <circle cx="220" cy="60" r="3" />
            <circle cx="280" cy="35" r="3" />
          </g>
          <g transform="translate(260, 45)">
            <rect x="0" y="0" width="55" height="24" rx="4" fill="rgba(34,197,94,0.2)" />
            <text x="8" y="16" fill="#4ade80" fontSize="11" fontWeight="bold">↑ 24.8%</text>
          </g>
          <text x="20" y="53" fill="rgba(255,255,255,0.4)" fontSize="8">$40K</text>
          <text x="20" y="83" fill="rgba(255,255,255,0.4)" fontSize="8">$30K</text>
          <text x="20" y="113" fill="rgba(255,255,255,0.4)" fontSize="8">$20K</text>
          <text x="20" y="143" fill="rgba(255,255,255,0.4)" fontSize="8">$10K</text>
          <text x="40" y="22" fill="rgba(255,255,255,0.8)" fontSize="11" fontWeight="600">Crecimiento de Portafolio</text>
        </>
      )
    },
    // Scene 3: Vault / Safe
    {
      id: 'vault',
      background: (
        <>
          <defs>
            <linearGradient id="vaultBg" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#1e293b" />
              <stop offset="100%" stopColor="#0f172a" />
            </linearGradient>
            <linearGradient id="vaultDoor" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#475569" />
              <stop offset="50%" stopColor="#334155" />
              <stop offset="100%" stopColor="#1e293b" />
            </linearGradient>
            <linearGradient id="vaultGold" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#fbbf24" />
              <stop offset="100%" stopColor="#d97706" />
            </linearGradient>
            <radialGradient id="handleShine" cx="30%" cy="30%">
              <stop offset="0%" stopColor="#94a3b8" />
              <stop offset="100%" stopColor="#475569" />
            </radialGradient>
          </defs>
          <rect width="340" height="180" fill="url(#vaultBg)" />
          <rect x="60" y="15" width="220" height="150" rx="8" fill="#334155" stroke="#475569" strokeWidth="4" />
          <rect x="75" y="30" width="190" height="120" rx="4" fill="url(#vaultDoor)" />
          <rect x="85" y="40" width="170" height="100" rx="2" fill="none" stroke="#475569" strokeWidth="2" />
          <circle cx="170" cy="90" r="35" fill="url(#handleShine)" stroke="#64748b" strokeWidth="3" />
          <circle cx="170" cy="90" r="28" fill="#334155" stroke="#475569" strokeWidth="2" />
          <circle cx="170" cy="90" r="8" fill="#1e293b" />
          <g stroke="#64748b" strokeWidth="3" strokeLinecap="round">
            <line x1="170" y1="65" x2="170" y2="75" />
            <line x1="170" y1="105" x2="170" y2="115" />
            <line x1="145" y1="90" x2="155" y2="90" />
            <line x1="185" y1="90" x2="195" y2="90" />
            <line x1="152" y1="72" x2="159" y2="79" />
            <line x1="181" y1="101" x2="188" y2="108" />
            <line x1="152" y1="108" x2="159" y2="101" />
            <line x1="181" y1="79" x2="188" y2="72" />
          </g>
          <g fill="#64748b">
            <rect x="265" y="50" width="15" height="12" rx="2" />
            <rect x="265" y="84" width="15" height="12" rx="2" />
            <rect x="265" y="118" width="15" height="12" rx="2" />
          </g>
          <g transform="translate(95, 55)">
            <rect x="0" y="0" width="45" height="60" rx="3" fill="#1e293b" stroke="#334155" strokeWidth="1" />
            <rect x="5" y="5" width="35" height="12" rx="2" fill="#0f172a" />
            <text x="10" y="14" fill="#22c55e" fontSize="8" fontFamily="monospace">****</text>
            {[0,1,2].map(row => (
              [0,1,2].map(col => (
                <rect key={`${row}-${col}`} x={5 + col * 12} y={22 + row * 12} width="10" height="10" rx="1" fill="#334155" />
              ))
            ))}
          </g>
          <g transform="translate(170, 145)">
            <text x="0" y="0" fill="url(#vaultGold)" fontSize="10" fontWeight="bold" textAnchor="middle">SWIFTGOLD</text>
            <text x="0" y="12" fill="rgba(255,255,255,0.4)" fontSize="7" textAnchor="middle">PROTECCION DE BOVEDA</text>
          </g>
        </>
      )
    },
    // Scene 4: Bank Building
    {
      id: 'building',
      background: (
        <>
          <defs>
            <linearGradient id="buildingBg" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#1e3a5f" />
              <stop offset="100%" stopColor="#0f172a" />
            </linearGradient>
            <linearGradient id="buildingFace" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#e2e8f0" />
              <stop offset="50%" stopColor="#f8fafc" />
              <stop offset="100%" stopColor="#cbd5e1" />
            </linearGradient>
            <linearGradient id="columnGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#94a3b8" />
              <stop offset="50%" stopColor="#e2e8f0" />
              <stop offset="100%" stopColor="#94a3b8" />
            </linearGradient>
            <linearGradient id="roofGold" x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" stopColor="#d97706" />
              <stop offset="100%" stopColor="#fbbf24" />
            </linearGradient>
          </defs>
          <rect width="340" height="180" fill="url(#buildingBg)" />
          <g fill="white" opacity="0.6">
            <circle cx="30" cy="25" r="1" />
            <circle cx="80" cy="40" r="1.2" />
            <circle cx="260" cy="20" r="1" />
            <circle cx="310" cy="45" r="1.2" />
            <circle cx="180" cy="15" r="1" />
          </g>
          <rect x="0" y="155" width="340" height="25" fill="#1e293b" />
          <rect x="0" y="155" width="340" height="3" fill="#334155" />
          <rect x="80" y="150" width="180" height="8" fill="#94a3b8" />
          <rect x="90" y="145" width="160" height="8" fill="#cbd5e1" />
          <rect x="100" y="140" width="140" height="8" fill="#e2e8f0" />
          <rect x="70" y="65" width="200" height="78" fill="url(#buildingFace)" />
          {[0,1,2,3,4,5].map(i => (
            <g key={i}>
              <rect x={85 + i * 32} y="75" width="12" height="65" fill="url(#columnGrad)" />
              <ellipse cx={91 + i * 32} cy="75" rx="6" ry="3" fill="#cbd5e1" />
              <rect x={85 + i * 32} y="135" width="12" height="5" fill="#94a3b8" />
            </g>
          ))}
          <polygon points="70,65 170,25 270,65" fill="url(#buildingFace)" stroke="#cbd5e1" strokeWidth="2" />
          <polygon points="85,65 170,35 255,65" fill="none" stroke="#94a3b8" strokeWidth="1" />
          <line x1="70" y1="65" x2="270" y2="65" stroke="url(#roofGold)" strokeWidth="3" />
          <text x="170" y="55" fill="#1e293b" fontSize="11" fontWeight="bold" textAnchor="middle" fontFamily="system-ui">SWIFTGOLD</text>
          <rect x="150" y="105" width="40" height="35" fill="#1e293b" rx="2" />
          <rect x="152" y="107" width="17" height="31" fill="#334155" />
          <rect x="171" y="107" width="17" height="31" fill="#334155" />
          <circle cx="167" cy="125" r="2" fill="url(#roofGold)" />
          <circle cx="173" cy="125" r="2" fill="url(#roofGold)" />
          {[0,1].map(i => (
            <g key={i}>
              <rect x={90 + i * 130} y="95" width="25" height="30" fill="#1e3a5f" rx="1" />
              <line x1={102.5 + i * 130} y1="95" x2={102.5 + i * 130} y2="125" stroke="#94a3b8" strokeWidth="1" />
              <line x1={90 + i * 130} y1="110" x2={115 + i * 130} y2="110" stroke="#94a3b8" strokeWidth="1" />
            </g>
          ))}
          <g>
            <rect x="45" y="120" width="4" height="35" fill="#475569" />
            <circle cx="47" cy="118" r="6" fill="#fbbf24" opacity="0.8" />
            <circle cx="47" cy="118" r="10" fill="#fbbf24" opacity="0.2" />
          </g>
          <g>
            <rect x="291" y="120" width="4" height="35" fill="#475569" />
            <circle cx="293" cy="118" r="6" fill="#fbbf24" opacity="0.8" />
            <circle cx="293" cy="118" r="10" fill="#fbbf24" opacity="0.2" />
          </g>
        </>
      )
    },
    // Scene 5: Security Shield
    {
      id: 'security',
      background: (
        <>
          <defs>
            <linearGradient id="secBg" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#0f172a" />
              <stop offset="100%" stopColor="#1e293b" />
            </linearGradient>
            <linearGradient id="shieldGold" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#fbbf24" />
              <stop offset="50%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#d97706" />
            </linearGradient>
            <linearGradient id="shieldInner" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#1e3a5f" />
              <stop offset="100%" stopColor="#0f172a" />
            </linearGradient>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          <rect width="340" height="180" fill="url(#secBg)" />
          <g stroke="rgba(59,130,246,0.1)" strokeWidth="1" fill="none">
            <path d="M20,30 L60,30 L60,60 L100,60" />
            <path d="M280,40 L320,40" />
            <path d="M30,140 L30,160 L80,160" />
            <path d="M260,130 L300,130 L300,160" />
            <path d="M40,90 L20,90" />
            <path d="M300,90 L320,90" />
            <circle cx="60" cy="30" r="3" fill="rgba(59,130,246,0.2)" />
            <circle cx="100" cy="60" r="3" fill="rgba(59,130,246,0.2)" />
            <circle cx="280" cy="40" r="3" fill="rgba(59,130,246,0.2)" />
            <circle cx="30" cy="140" r="3" fill="rgba(59,130,246,0.2)" />
            <circle cx="300" cy="130" r="3" fill="rgba(59,130,246,0.2)" />
          </g>
          <ellipse cx="170" cy="95" rx="70" ry="75" fill="rgba(251,191,36,0.05)" />
          <g transform="translate(170, 90)" filter="url(#glow)">
            <path
              d="M0,-65 L55,-45 L55,10 C55,45 0,70 0,70 C0,70 -55,45 -55,10 L-55,-45 Z"
              fill="url(#shieldGold)"
              stroke="#fcd34d"
              strokeWidth="2"
            />
            <path
              d="M0,-55 L45,-38 L45,8 C45,38 0,58 0,58 C0,58 -45,38 -45,8 L-45,-38 Z"
              fill="url(#shieldInner)"
            />
            <path
              d="M-18,0 L-5,15 L22,-15"
              fill="none"
              stroke="#22c55e"
              strokeWidth="6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
          <text x="170" y="172" fill="rgba(255,255,255,0.8)" fontSize="10" fontWeight="600" textAnchor="middle">TRANSACCION PROTEGIDA</text>
          <g transform="translate(60, 80)" fill="#3b82f6" opacity="0.6">
            <rect x="3" y="8" width="14" height="12" rx="2" />
            <path d="M5,8 L5,5 A5,5 0 0 1 15,5 L15,8" fill="none" stroke="#3b82f6" strokeWidth="2" />
          </g>
          <g transform="translate(260, 80)" fill="#3b82f6" opacity="0.6">
            <rect x="3" y="8" width="14" height="12" rx="2" />
            <path d="M5,8 L5,5 A5,5 0 0 1 15,5 L15,8" fill="none" stroke="#3b82f6" strokeWidth="2" />
          </g>
          <circle cx="90" cy="50" r="2" fill="#fbbf24" opacity="0.4" />
          <circle cx="250" cy="60" r="2" fill="#fbbf24" opacity="0.3" />
          <circle cx="70" cy="130" r="1.5" fill="#3b82f6" opacity="0.5" />
          <circle cx="270" cy="140" r="1.5" fill="#3b82f6" opacity="0.5" />
        </>
      )
    }
  ]

  const generateNewPuzzle = useCallback(() => {
    const newX = 130 + Math.random() * 100
    const newY = 40 + Math.random() * 50
    setTargetPosition({ x: newX, y: newY })
    setSliderPosition(0)
    setIsVerified(false)
    setIsFailed(false)
    setShowSuccess(false)
    setSceneIndex((prev) => (prev + 1) % scenes.length)
  }, [scenes.length])

  useEffect(() => {
    generateNewPuzzle()
  }, [])

  const handleStart = (e) => {
    if (isVerified || isLoading) return
    e.preventDefault()
    setIsDragging(true)
    setIsFailed(false)
  }

  const handleMove = useCallback((clientX) => {
    if (!isDragging || isVerified || isLoading) return

    const container = containerRef.current
    if (!container) return

    const rect = container.getBoundingClientRect()
    const x = clientX - rect.left - 24
    const newPosition = Math.max(0, Math.min(x, maxSliderWidth))
    setSliderPosition(newPosition)
  }, [isDragging, isVerified, isLoading, maxSliderWidth])

  const handleEnd = useCallback(() => {
    if (!isDragging) return
    setIsDragging(false)

    const distance = Math.abs(sliderPosition - targetPosition.x)

    if (distance <= tolerance) {
      setIsLoading(true)
      setSliderPosition(targetPosition.x)
      setTimeout(() => {
        setIsLoading(false)
        setIsVerified(true)
        setTimeout(() => {
          setShowSuccess(true)
          onVerified?.()
        }, 100)
      }, 600)
    } else {
      setIsFailed(true)
      setAttempts((prev) => prev + 1)
      setTimeout(() => {
        setSliderPosition(0)
        setIsFailed(false)
        if (attempts >= 2) {
          onFailed?.()
        }
      }, 400)
    }
  }, [isDragging, sliderPosition, targetPosition.x, tolerance, attempts, onVerified, onFailed])

  useEffect(() => {
    const onMouseMove = (e) => handleMove(e.clientX)
    const onTouchMove = (e) => handleMove(e.touches[0].clientX)
    const onEnd = () => handleEnd()

    if (isDragging) {
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onEnd)
      document.addEventListener('touchmove', onTouchMove, { passive: true })
      document.addEventListener('touchend', onEnd)
    }

    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onEnd)
      document.removeEventListener('touchmove', onTouchMove)
      document.removeEventListener('touchend', onEnd)
    }
  }, [isDragging, handleMove, handleEnd])

  const puzzlePath = `
    M 0 0
    L 16 0
    C 16 -6, 24 -6, 24 0
    L 24 0
    C 24 -6, 32 -6, 32 0
    L 48 0
    L 48 16
    C 54 16, 54 24, 48 24
    L 48 24
    C 54 24, 54 32, 48 32
    L 48 48
    L 32 48
    C 32 54, 24 54, 24 48
    L 24 48
    C 24 54, 16 54, 16 48
    L 0 48
    L 0 32
    C -6 32, -6 24, 0 24
    L 0 24
    C -6 24, -6 16, 0 16
    Z
  `

  return (
    <div className="relative w-full max-w-sm mx-auto">
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes success-pop {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes glow-pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.7; }
        }
        @keyframes border-glow {
          0%, 100% { border-color: rgba(251, 191, 36, 0.3); }
          50% { border-color: rgba(251, 191, 36, 0.6); }
        }
        .captcha-shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent);
          background-size: 200% 100%;
          animation: shimmer 2.5s infinite;
        }
        .captcha-success-pop {
          animation: success-pop 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
        }
        .captcha-glow-pulse {
          animation: glow-pulse 3s ease-in-out infinite;
        }
        .captcha-border-glow {
          animation: border-glow 2s ease-in-out infinite;
        }
      `}</style>

      {/* Glow effect behind card */}
      <div className="absolute -inset-2 bg-gradient-to-r from-amber-500/10 via-yellow-500/10 to-amber-500/10 rounded-3xl blur-2xl captcha-glow-pulse" />

      <div className="relative bg-slate-900/95 backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden border border-amber-500/20 captcha-border-glow">
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between bg-gradient-to-r from-slate-800/50 to-slate-900/50 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-amber-500/30 rounded-xl blur-md" />
              <div className="relative bg-gradient-to-br from-amber-400 to-amber-600 p-2.5 rounded-xl">
                <Lock className="w-4 h-4 text-white" />
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-white font-bold text-sm tracking-wide">Swift Gold</span>
                <span className="text-amber-400/60 text-xs">|</span>
                <span className="text-slate-400 text-xs font-medium">Verificacion</span>
              </div>
              <p className="text-slate-500 text-xs mt-0.5">Complete el puzzle para continuar</p>
            </div>
          </div>
          <button
            onClick={generateNewPuzzle}
            className="p-2.5 hover:bg-white/5 rounded-xl transition-all duration-300 hover:rotate-180 group border border-transparent hover:border-white/10"
            title="Generar nuevo puzzle"
          >
            <RefreshCw className="w-4 h-4 text-slate-500 group-hover:text-amber-400 transition-colors" />
          </button>
        </div>

        {/* Puzzle Area */}
        <div
          ref={containerRef}
          className="relative w-full h-44 overflow-hidden bg-slate-950"
        >
          {/* SVG Scene */}
          <svg
            viewBox="0 0 340 180"
            className="w-full h-full"
            preserveAspectRatio="xMidYMid slice"
          >
            <defs>
              <clipPath id="holeClip">
                <rect width="340" height="180" />
                <path
                  d={puzzlePath}
                  transform={`translate(${targetPosition.x}, ${targetPosition.y})`}
                  clipRule="evenodd"
                />
              </clipPath>
              <filter id="holeShadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="2" dy="2" stdDeviation="4" floodColor="rgba(0,0,0,0.6)" />
              </filter>
            </defs>

            {/* Background with hole */}
            <g clipPath="url(#holeClip)">
              {scenes[sceneIndex].background}
            </g>

            {/* Hole outline (dark area) */}
            <g transform={`translate(${targetPosition.x}, ${targetPosition.y})`}>
              <path
                d={puzzlePath}
                fill="rgba(0,0,0,0.6)"
                filter="url(#holeShadow)"
              />
              <path
                d={puzzlePath}
                fill="none"
                stroke="rgba(251,191,36,0.3)"
                strokeWidth="1"
              />
            </g>
          </svg>

          {/* Draggable Piece */}
          {!isVerified && (
            <div
              className={`absolute transition-all ${isDragging ? 'duration-0' : 'duration-300 ease-out'}`}
              style={{
                left: sliderPosition,
                top: targetPosition.y,
                width: pieceSize,
                height: pieceSize,
                transform: isDragging ? 'scale(1.08)' : 'scale(1)',
              }}
            >
              <svg
                width={pieceSize + 12}
                height={pieceSize + 12}
                viewBox="-6 -6 60 60"
                style={{
                  filter: isDragging
                    ? 'drop-shadow(0 10px 20px rgba(0,0,0,0.6))'
                    : 'drop-shadow(0 4px 10px rgba(0,0,0,0.5))',
                  marginLeft: -6,
                  marginTop: -6,
                }}
              >
                <defs>
                  <clipPath id="movingPieceClip">
                    <path d={puzzlePath} />
                  </clipPath>
                </defs>
                <g clipPath="url(#movingPieceClip)">
                  <g transform={`translate(${-targetPosition.x}, ${-targetPosition.y})`}>
                    <svg viewBox="0 0 340 180" width="340" height="180">
                      {scenes[sceneIndex].background}
                    </svg>
                  </g>
                </g>
                <path
                  d={puzzlePath}
                  fill="none"
                  stroke="rgba(255,255,255,0.9)"
                  strokeWidth="2"
                />
              </svg>
            </div>
          )}

          {/* Completed piece (when verified) */}
          {isVerified && (
            <div
              className="absolute"
              style={{
                left: targetPosition.x,
                top: targetPosition.y,
                width: pieceSize,
                height: pieceSize,
              }}
            >
              <svg
                width={pieceSize + 12}
                height={pieceSize + 12}
                viewBox="-6 -6 60 60"
                style={{
                  marginLeft: -6,
                  marginTop: -6,
                }}
              >
                <defs>
                  <clipPath id="completedPieceClip">
                    <path d={puzzlePath} />
                  </clipPath>
                </defs>
                <g clipPath="url(#completedPieceClip)">
                  <g transform={`translate(${-targetPosition.x}, ${-targetPosition.y})`}>
                    <svg viewBox="0 0 340 180" width="340" height="180">
                      {scenes[sceneIndex].background}
                    </svg>
                  </g>
                </g>
              </svg>
            </div>
          )}

          {/* Loading Overlay */}
          {isLoading && (
            <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center">
              <div className="relative">
                <div className="w-10 h-10 border-2 border-amber-500/30 border-t-amber-400 rounded-full animate-spin" />
              </div>
            </div>
          )}

          {/* Success Overlay */}
          {showSuccess && (
            <div className="absolute inset-0 bg-emerald-500/10 backdrop-blur-sm flex items-center justify-center">
              <div className="captcha-success-pop">
                <div className="relative">
                  <div className="absolute inset-0 bg-emerald-400 rounded-full blur-xl opacity-40" />
                  <div className="relative bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full p-4 shadow-lg shadow-emerald-500/30 border border-emerald-300/30">
                    <Check className="w-8 h-8 text-white" strokeWidth={3} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Slider Area */}
        <div className="p-5 bg-gradient-to-b from-slate-900/50 to-slate-950/50">
          <div
            className={`relative h-14 rounded-xl overflow-hidden transition-all duration-500 ${
              isVerified
                ? 'bg-emerald-500/10 border-emerald-500/30'
                : isFailed
                  ? 'bg-red-500/10 border-red-500/30'
                  : 'bg-slate-800/50 border-slate-700/50'
            } border`}
          >
            {/* Progress fill */}
            <div
              className={`absolute inset-y-0 left-0 transition-all ${isDragging ? 'duration-0' : 'duration-300'} ${
                isVerified
                  ? 'bg-gradient-to-r from-emerald-500/30 to-emerald-400/30'
                  : 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20'
              }`}
              style={{ width: `${(sliderPosition / maxSliderWidth) * 100}%` }}
            />

            {/* Shimmer effect */}
            {!isVerified && !isFailed && (
              <div className="absolute inset-0 captcha-shimmer pointer-events-none" />
            )}

            {/* Center text */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              {isVerified ? (
                <span className="text-emerald-400 font-semibold text-sm flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  Verificacion exitosa
                </span>
              ) : isFailed ? (
                <span className="text-red-400 font-medium text-sm flex items-center gap-2 animate-pulse">
                  <X className="w-5 h-5" />
                  Intente de nuevo
                </span>
              ) : sliderPosition < 10 ? (
                <div className="flex items-center gap-2 text-slate-500 text-sm">
                  <ChevronRight className="w-4 h-4 animate-pulse text-amber-500/70" />
                  <span>Deslice para verificar</span>
                  <ChevronRight className="w-4 h-4 animate-pulse text-amber-500/70" />
                </div>
              ) : null}
            </div>

            {/* Slider handle */}
            {!isVerified && (
              <div
                className={`absolute top-1.5 bottom-1.5 w-12 rounded-lg flex items-center justify-center cursor-grab active:cursor-grabbing transition-all ${isDragging ? 'duration-0' : 'duration-300'} ${
                  isDragging
                    ? 'bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg shadow-amber-500/40 scale-105'
                    : isFailed
                      ? 'bg-gradient-to-br from-red-400 to-red-600 shadow-lg shadow-red-500/30'
                      : 'bg-gradient-to-br from-amber-400 to-amber-600 hover:from-amber-300 hover:to-amber-500 shadow-lg shadow-amber-500/20'
                }`}
                style={{
                  left: `${sliderPosition + 6}px`,
                }}
                onMouseDown={handleStart}
                onTouchStart={handleStart}
              >
                <div className="flex items-center gap-0.5">
                  <div className="w-0.5 h-5 bg-white/50 rounded-full" />
                  <div className="w-0.5 h-5 bg-white/50 rounded-full" />
                  <div className="w-0.5 h-5 bg-white/50 rounded-full" />
                </div>
              </div>
            )}
          </div>

          {/* Attempts counter */}
          {attempts > 0 && !isVerified && (
            <p className="text-slate-600 text-xs text-center mt-3 flex items-center justify-center gap-1">
              <span className="w-1.5 h-1.5 bg-slate-600 rounded-full" />
              {attempts} intento{attempts > 1 ? 's' : ''} fallido{attempts > 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-4 flex items-center justify-center gap-2 border-t border-white/5 pt-3">
          <Shield className="w-3 h-3 text-amber-500/60" />
          <span className="text-slate-600 text-xs">Protegido por</span>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-500 font-bold text-xs tracking-wide">
            Swift Gold Fiduciary
          </span>
        </div>
      </div>
    </div>
  )
}
